import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Brush, Award, Users } from "lucide-react";
import useSEO from "@/lib/useSEO";
import { SITE } from "@/lib/site";

export default function About() {
    useSEO({
        title: `About — ${SITE.brand}`,
        description: `Locally owned painting company in ${SITE.serviceArea}. Learn how CPR Paint & Coatings approaches quality, reliability, and the trades.`,
    });

    const values = [
        {
            icon: Brush,
            title: "Quality is the prep",
            body: "What you don't see — patching, caulking, priming, masking — is what makes the finish last. We don't shortcut it.",
        },
        {
            icon: ShieldCheck,
            title: "Reliability is the deal",
            body: "Start dates we hit. End dates we hit. Daily updates. No ghosting between bid and brush.",
        },
        {
            icon: Users,
            title: "Respect your home",
            body: "Floors covered, surfaces masked, dust contained. Swept and walked before we hand it back to you.",
        },
        {
            icon: Award,
            title: "Stand behind it",
            body: "Written warranty on workmanship. If something isn't right, we come back and make it right.",
        },
    ];

    return (
        <main data-testid="page-about">
            {/* Hero */}
            <section
                style={{ background: "var(--brand-cream)" }}
                className="section-pad"
            >
                <div
                    className="brand-container fade-rise"
                    style={{
                        display: "grid",
                        gap: "3rem",
                        gridTemplateColumns:
                            "minmax(0, 1.1fr) minmax(0, 0.9fr)",
                        alignItems: "center",
                    }}
                >
                    <div>
                        <span className="eyebrow">About the company</span>
                        <h1
                            className="h1"
                            style={{
                                marginTop: "1rem",
                                marginBottom: "1.25rem",
                            }}
                            data-testid="about-headline"
                        >
                            A painting company built on{" "}
                            <span style={{ color: "var(--brand-red)" }}>
                                doing the work right
                            </span>
                            .
                        </h1>
                        <p
                            style={{
                                color: "var(--brand-muted)",
                                fontSize: "1.125rem",
                                marginBottom: "1.25rem",
                            }}
                        >
                            CPR Paint & Coatings was started because the trades
                            deserve a higher standard. Clean prep. Honest
                            estimates. Crews that show up when they said they
                            would and treat your space like their own.
                        </p>
                        <p
                            style={{
                                color: "var(--brand-muted)",
                                fontSize: "1.0625rem",
                            }}
                        >
                            We're proudly serving Utah and surrounding states, as
                            well as our friends and neighbors here at home.
                            Whether it's a tenant improvement, a multi-building
                            commercial repaint, or a custom home, the standard is
                            the same.
                        </p>
                    </div>
                    <div
                        style={{
                            aspectRatio: "4 / 5",
                            borderRadius: 16,
                            overflow: "hidden",
                            boxShadow:
                                "0 20px 50px rgba(26,26,26,0.15)",
                            border: "8px solid #fff",
                            background: "var(--brand-surface)",
                        }}
                        data-testid="about-image-frame"
                    >
                        <img
                            src="/img/about-palette.png"
                            alt="Paint color swatches and brushes arranged on a workbench"
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                            }}
                            onError={(e) => {
                                e.currentTarget.style.display = "none";
                            }}
                        />
                    </div>
                </div>
            </section>

            {/* Founder bio */}
            <section
                className="section-pad"
                style={{
                    background: "var(--brand-cream)",
                    borderTop: "1px solid var(--brand-surface)",
                }}
                data-testid="about-founder-section"
            >
                <div className="brand-container">
                    <div style={{ marginBottom: "2.5rem", maxWidth: 640 }}>
                        <span className="eyebrow">Meet the founder</span>
                        <h2
                            className="h2"
                            style={{ marginTop: "0.85rem" }}
                        >
                            Todd Beagley knows the trade from the ground up.
                        </h2>
                    </div>
                    <div
                        style={{
                            display: "grid",
                            gap: "1.5rem",
                            gridTemplateColumns:
                                "repeat(auto-fit, minmax(260px, 1fr))",
                            maxWidth: 880,
                        }}
                    >
                        <p
                            style={{
                                color: "var(--brand-muted)",
                                fontSize: "1.0625rem",
                            }}
                        >
                            30+ years in professional painting. Todd started
                            pushing a broom, worked and ran paint crews, and
                            learned commercial painting the way you can only
                            learn it â on the job, from the ground up.
                        </p>
                        <p
                            style={{
                                color: "var(--brand-muted)",
                                fontSize: "1.0625rem",
                            }}
                        >
                            He grew up in the business and now runs his own
                            company. While private-equity firms buy up trades
                            companies run by owners who don't know the work, Todd
                            knows it firsthand â and that's what sets CPR Paint &
                            Coatings apart. This is quality from someone who
                            actually understands the trade.
                        </p>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section
                className="section-pad"
                style={{ background: "#fff" }}
                data-testid="about-values-section"
            >
                <div className="brand-container">
                    <div style={{ marginBottom: "2.5rem", maxWidth: 640 }}>
                        <span className="eyebrow">What we stand for</span>
                        <h2 className="h2" style={{ marginTop: "0.85rem" }}>
                            Four things we don't compromise on.
                        </h2>
                    </div>

                    <div
                        style={{
                            display: "grid",
                            gap: "1.25rem",
                            gridTemplateColumns:
                                "repeat(auto-fit, minmax(240px, 1fr))",
                        }}
                    >
                        {values.map(({ icon: Icon, title, body }) => (
                            <div
                                key={title}
                                className="brand-card"
                                data-testid={`about-value-${title
                                    .toLowerCase()
                                    .replace(/[^a-z]+/g, "-")
                                    .replace(/^-|-$/g, "")}`}
                            >
                                <div
                                    style={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: 10,
                                        background: "rgba(210, 5, 28, 0.08)",
                                        color: "var(--brand-red)",
                                        display: "grid",
                                        placeItems: "center",
                                        marginBottom: "1rem",
                                    }}
                                >
                                    <Icon size={22} />
                                </div>
                                <h3 className="h3">{title}</h3>
                                <p
                                    style={{
                                        marginTop: "0.6rem",
                                        color: "var(--brand-muted)",
                                        fontSize: "0.95rem",
                                    }}
                                >
                                    {body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Trust strip */}
            <section
                className="section-pad-sm"
                style={{
                    background: "var(--brand-cream)",
                    borderTop: "1px solid var(--brand-surface)",
                }}
                data-testid="about-trust-strip"
            >
                <div
                    className="brand-container"
                    style={{
                        display: "grid",
                        gap: "1rem",
                        gridTemplateColumns:
                            "repeat(auto-fit, minmax(180px, 1fr))",
                        textAlign: "center",
                    }}
                >
                    {[
                        { k: "Licensed", v: "& bonded contractor" },
                        { k: "Insured", v: "general liability + workers' comp" },
                        { k: "Utah", v: "based & serving the West" },
                        { k: "Written", v: "estimates & warranty" },
                    ].map((it) => (
                        <div
                            key={it.k}
                            style={{
                                padding: "1.25rem 0.5rem",
                                borderLeft: "2px solid var(--brand-red)",
                            }}
                        >
                            <div
                                style={{
                                    fontFamily: "var(--font-display)",
                                    fontWeight: 800,
                                    fontSize: "1.5rem",
                                    color: "var(--brand-dark)",
                                }}
                            >
                                {it.k}
                            </div>
                            <div
                                style={{
                                    color: "var(--brand-muted)",
                                    fontSize: "0.9rem",
                                    marginTop: "0.25rem",
                                }}
                            >
                                {it.v}
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section
                className="section-pad"
                style={{ background: "#fff" }}
                data-testid="about-cta-section"
            >
                <div
                    className="brand-container"
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "1rem",
                        alignItems: "center",
                        textAlign: "center",
                    }}
                >
                    <h2
                        className="h2"
                        style={{
                            maxWidth: 700,
                            fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)",
                        }}
                    >
                        Meet the team — get your free estimate.
                    </h2>
                    <p style={{ color: "var(--brand-muted)", maxWidth: 520 }}>
                        We'd love to walk your space and put real numbers on the
                        work. No pressure, no surprises.
                    </p>
                    <Link
                        to="/contact"
                        className="btn btn-primary"
                        data-testid="about-cta-button"
                        style={{ marginTop: "0.25rem" }}
                    >
                        Request an estimate
                        <ArrowRight size={18} />
                    </Link>
                </div>
            </section>
        </main>
    );
}
